import {DomainObject} from 'shared/store';
import { action, computed, observable, makeObservable } from 'mobx';
import {Employee} from 'main/stores/employees';
import TimesheetEntry from 'main/stores/time_tracking/TimesheetEntry';
import TimeTrackingPayPeriod from 'main/stores/time_tracking/TimeTrackingPayPeriod';
import TimeTrackingDimension from 'main/stores/time_tracking/TimeTrackingDimension';
import _ from 'lodash';
import moment from 'moment';
import Absence from 'main/stores/time_off/Absence';
import Holiday from 'main/stores/holidays/Holiday';

class Timesheet extends DomainObject {
  entries = [];
  absences = [];
  holidays = [];
  dimensions = [];
  shifts = [];
  totals = {};
  approved = false;
  canCreateEntries;
  manuallyEnterHours;
  canApprove;
  timeClockEnabled;
  collectEmployeeLocation;
  geofencingEnabled;
  schedulingEnabled;
  assignedDimensionValueIds = [];

  _relations = {
    employee: Employee,
    entries: [TimesheetEntry],
    absences: [Absence],
    holidays: [Holiday],
    dimensions: [TimeTrackingDimension],
    payPeriod: TimeTrackingPayPeriod
  };

  constructor(other) {
    super();

    makeObservable(this, {
      entries: observable,
      absences: observable,
      holidays: observable,
      dimensions: observable,
      shifts: observable,
      totals: observable,
      approved: observable,
      canCreateEntries: observable,
      manuallyEnterHours: observable,
      canApprove: observable,
      timeClockEnabled: observable,
      collectEmployeeLocation: observable,
      geofencingEnabled: observable,
      schedulingEnabled: observable,
      assignedDimensionValueIds: observable,
      totalHours: computed,
      fillMissingDates: action,
      addEntriesForUnmatchedShifts: action,
      addEntry: action,
      removeEntry: action,
      applyShift: action,
      updateTotals: action,
      mergeApproval: action
    });

    this.merge(other);
  }

  get totalHours() {
    return Math.round(_.sumBy(this.entries, 'hours') * 100) / 100;
  }

  rows() {
    const entriesAndAbsences = this.entries.slice();
    this.absences.forEach(absence => {
      const index = _.findLastIndex(entriesAndAbsences, {date: absence.date});
      entriesAndAbsences.splice(index + 1, 0, absence);
    });

    const rows = [];
    let lastEntry = null;
    _.forEach(entriesAndAbsences, object => {
      const holiday = _.get(_.filter(this.holidays, {date: object.date}), '0');

      const firstEntryForDay = object.date.toISOString() !== (lastEntry && lastEntry.date.toISOString());
      const hasHoliday = !!holiday;

      if (firstEntryForDay && hasHoliday) {
        holiday.showDate = true;
        holiday.rowClasses = 'bg-alabaster-important no-bottom-border';
        object.showDate = false;
        object.rowClasses = 'bg-alabaster-important';
        rows.push(holiday);
      } else if (hasHoliday) {
        object.rowClasses = 'bg-alabaster-important';
        lastEntry.rowClasses += ' no-bottom-border';
      } else if (firstEntryForDay) {
        object.showDate = true;
        object.rowClasses = '';
      } else {
        object.rowClasses = '';
        lastEntry.rowClasses += ' no-bottom-border';
      }

      lastEntry = object;

      rows.push(object);
    });

    return rows;
  }

  entryFor(uuid) {
    return _.find(this.entries, {uuid});
  }

  entriesOn(date) {
    return this.entries.filter(entry => entry.date.toISOString() === date.toISOString());
  }

  shiftsOn(date) {
    return _.sortBy(this.shifts.filter(shift => shift.date.toISOString() === date.toISOString()), 'startTime');
  }

  shiftFor(entry) {
    const index = _.findIndex(this.entriesOn(entry.date), {uuid: entry.uuid});
    if (index === -1) return null;

    return this.shiftsOn(entry.date)[index];
  }

  fillMissingDates(payPeriod) {
    for (let i = 0; moment(payPeriod.startDate).add(i, 'd') <= moment(payPeriod.endDate); i++) {
      const date = moment(payPeriod.startDate).add(i, 'd');
      if (!_.find(this.entries, (entry) => entry.date.toISOString() === date.toISOString())) {
        const previousDate = moment(payPeriod.startDate).add(i - 1, 'd');
        const previousDateIndex = _.findLastIndex(this.entries, (entry) => entry.date.toISOString() === previousDate.toISOString());
        this.entries.splice(previousDateIndex + 1, 0, new TimesheetEntry({date: date.toDate()}));
      }
    }
  }

  addEntriesForUnmatchedShifts() {
    _.forEach(_.groupBy(this.shifts, shift => shift.date.toISOString()), shiftsOnDate => {
      const {date} = shiftsOnDate[0];
      _.times(shiftsOnDate.length - this.entriesOn(date).length, () => this.addEntry(date));
    });
  }

  addEntry(date) {
    const index = _.findLastIndex(this.entries, {date});
    this.entries.splice(
      index + 1,
      0,
      new TimesheetEntry({date, showDate: false})
    );
  }

  removeEntry(entry, payPeriod) {
    const indexOfEntry = _.findIndex(this.entries, {uuid: entry.uuid});
    this.entries.splice(indexOfEntry, 1);

    const minimumRowsForDate = Math.max(this.shiftsOn(entry.date).length, 1);
    if (this.entriesOn(entry.date).length < minimumRowsForDate && entry.date >= payPeriod.startDate) {
      this.entries.splice(indexOfEntry, 0, new TimesheetEntry({date: entry.date}));
    }
  }

  applyShift(entry) {
    const shift = this.shiftFor(entry);
    const hoursOrTimes = this.manuallyEnterHours ?
      {manuallyEnteredHours: _.round(shift.paidHours, 2)} :
      {startTimeString: shift.startTimeString, endTimeString: shift.endTimeString, unpaidBreakMinutes: shift.unpaidBreakMinutes};

    entry.shiftsApplied++;
    entry.dimensionValues = shift.dimensionValues.filter(value => this.assignedDimensionValueIds.includes(value.id));
    entry.merge({...hoursOrTimes, note: shift.note});
  }

  updateTotals(totals) {
    if (totals.calculatedAt > this.totals.calculatedAt) {
      this.totals = totals;
    }
  }

  mergeApproval(approvedTimesheet) {
    this.approved = approvedTimesheet.approved;
    this.entries.forEach(entry => {
      entry.merge(_.find(approvedTimesheet.entries, {id: entry.id}));
    });
  }

  async flushPendingEntryAutosaves() {
    await Promise.all(this.entries.map(async entry => {
      if (!entry.autosaver) { return; }

      await entry.autosaver.flush();
    }));
  }

  startTimeChangedSinceApproval(entry) {
    return this.wasEverApproved && (entry.startTime && !moment(entry.approvedStartTime).isSame(moment(entry.startTime)));
  }

  endTimeChangedSinceApproval(entry) {
    return this.wasEverApproved && ((entry.endTime && !moment(entry.approvedEndTime).isSame(moment(entry.endTime))) || (!entry.endTime && entry.approvedEndTime));
  }

  manuallyEnteredHoursChangedSinceApproval(entry) {
    return this.wasEverApproved && (entry.approvedManuallyEnteredHours !== null && parseFloat(entry.manuallyEnteredHours) !== parseFloat(entry.approvedManuallyEnteredHours));
  }

  holidayNameById(id) {
    return _.find(this.holidays, {id}).name;
  }
}

export default Timesheet;
